import { cx } from "@unizpay/ui";
import { useId } from "react";

const LENGTH = 6;

/**
 * A 6-digit PIN (masked) or emailed code (shown) as six boxes, filled as it is typed. One real
 * input sits over the boxes (digits only), so paste, backspace, one-time-code autofill and screen
 * readers behave as usual.
 * `onComplete` fires when the sixth digit is typed.
 */
export function PinBoxes({
  label,
  value,
  onChange,
  onComplete,
  error,
  disabled,
  masked = true,
  autoFocus = true,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  onComplete?: (v: string) => void;
  error?: string | null;
  disabled?: boolean;
  /** Dots for a PIN; digits for an emailed code. */
  masked?: boolean;
  autoFocus?: boolean;
}) {
  const errorId = useId();
  return (
    <div>
      <div className="relative inline-flex gap-2">
        {Array.from({ length: LENGTH }, (_, i) => (
          <span
            // biome-ignore lint/suspicious/noArrayIndexKey: fixed positions
            key={i}
            aria-hidden="true"
            className={cx(
              "flex size-11 items-center justify-center rounded-md border text-xl",
              error ? "border-danger" : i === value.length ? "border-accent" : "border-line-strong",
              disabled && "opacity-60",
            )}
          >
            {i < value.length ? (masked ? "•" : value[i]) : ""}
          </span>
        ))}
        <input
          type={masked ? "password" : "text"}
          inputMode="numeric"
          autoComplete={masked ? "off" : "one-time-code"}
          maxLength={LENGTH}
          aria-label={label}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? errorId : undefined}
          disabled={disabled}
          // biome-ignore lint/a11y/noAutofocus: each step asks for one PIN, typed straight away
          autoFocus={autoFocus}
          value={value}
          onChange={(e) => {
            const next = e.target.value.replace(/\D/g, "").slice(0, LENGTH);
            onChange(next);
            if (next.length === LENGTH) onComplete?.(next);
          }}
          className="absolute inset-0 h-full w-full cursor-text opacity-0"
        />
      </div>
      {error && (
        <p id={errorId} role="alert" className="mt-2 text-[0.8125rem] text-danger">
          {error}
        </p>
      )}
    </div>
  );
}
